export const EQUIPMENT_ERROR_CODES = [
  "EQUIPMENT_AUTH_REQUIRED",
  "EQUIPMENT_FORBIDDEN",
  "EQUIPMENT_NOT_FOUND",
  "EQUIPMENT_INVALID_STATE",
  "EQUIPMENT_INVALID_KIND",
  "EQUIPMENT_INVALID_TYPE",
  "EQUIPMENT_NAME_REQUIRED",
  "EQUIPMENT_ASSET_REQUIRED",
  "EQUIPMENT_ITEM_REQUIRED",
  "EQUIPMENT_WORKER_REQUIRED",
  "EQUIPMENT_ORG_REQUIRED",
  "EQUIPMENT_WORKER_NOT_IN_ORG",
  "EQUIPMENT_NOT_COUNTERPARTY",
  "EQUIPMENT_ALREADY_PENDING",
  "EQUIPMENT_NOT_AVAILABLE",
  "EQUIPMENT_RETIRED",
  "EQUIPMENT_PHOTO_INVALID",
  "EQUIPMENT_PHOTO_TOO_LARGE",
  "EQUIPMENT_PHOTO_TOO_MANY",
  "EQUIPMENT_NOTES_TOO_LONG",
  "EQUIPMENT_REJECTION_REASON_REQUIRED",
  "EQUIPMENT_SERIAL_TAKEN",
  "EQUIPMENT_STATUS_VIA_RPC",
  "EQUIPMENT_PROTOCOL_VIA_RPC",
  "EQUIPMENT_RPC_FAILED",
] as const;

export type EquipmentErrorCode = (typeof EQUIPMENT_ERROR_CODES)[number] | string;

export class EquipmentApiError extends Error {
  readonly code: EquipmentErrorCode;

  constructor(code: EquipmentErrorCode, message?: string) {
    super(message ?? equipmentErrorMessagePl(code));
    this.name = "EquipmentApiError";
    this.code = code;
  }
}

export function isMissingEquipmentRelation(error: unknown): boolean {
  const rec = error && typeof error === "object" ? (error as Record<string, unknown>) : null;
  const code = String(rec?.code ?? "");
  const message = `${rec?.message ?? ""} ${rec?.details ?? ""}`.toLowerCase();
  return (
    code === "PGRST205" ||
    code === "42P01" ||
    ((message.includes("schema cache") || message.includes("does not exist")) &&
      message.includes("equipment_"))
  );
}

export function equipmentErrorMessagePl(code: string): string {
  switch (code) {
    case "EQUIPMENT_AUTH_REQUIRED":
      return "Musisz być zalogowany.";
    case "EQUIPMENT_FORBIDDEN":
      return "Brak uprawnień do tej operacji na sprzęcie.";
    case "EQUIPMENT_NOT_FOUND":
      return "Nie znaleziono sprzętu lub protokołu.";
    case "EQUIPMENT_INVALID_STATE":
      return "Tego przedmiotu nie można teraz przekazać ani zdać.";
    case "EQUIPMENT_INVALID_KIND":
      return "Nieprawidłowy rodzaj sprzętu.";
    case "EQUIPMENT_INVALID_TYPE":
      return "Nieprawidłowy typ przedmiotu.";
    case "EQUIPMENT_NAME_REQUIRED":
      return "Podaj nazwę sprzętu.";
    case "EQUIPMENT_ASSET_REQUIRED":
      return "Wybierz narzędzie ze stanu firmy.";
    case "EQUIPMENT_ITEM_REQUIRED":
      return "Wybierz przedmiot.";
    case "EQUIPMENT_WORKER_REQUIRED":
      return "Wybierz pracownika.";
    case "EQUIPMENT_ORG_REQUIRED":
      return "Brak organizacji.";
    case "EQUIPMENT_WORKER_NOT_IN_ORG":
      return "Pracownik nie należy do tej organizacji.";
    case "EQUIPMENT_NOT_COUNTERPARTY":
      return "Tę czynność musi potwierdzić druga strona protokołu.";
    case "EQUIPMENT_ALREADY_PENDING":
      return "Ten przedmiot ma już oczekujący protokół.";
    case "EQUIPMENT_NOT_AVAILABLE":
      return "Narzędzie nie jest wolne — jest u kogoś albo wycofane.";
    case "EQUIPMENT_RETIRED":
      return "Wycofanego narzędzia nie można przekazać.";
    case "EQUIPMENT_PHOTO_INVALID":
      return "Zdjęcie musi być w formacie JPG, PNG, WEBP lub HEIC.";
    case "EQUIPMENT_PHOTO_TOO_LARGE":
      return "Zdjęcie jest zbyt duże (maks. 10 MB).";
    case "EQUIPMENT_PHOTO_TOO_MANY":
      return "Można dołączyć maksymalnie 5 zdjęć.";
    case "EQUIPMENT_NOTES_TOO_LONG":
      return "Opis stanu może mieć maksymalnie 2000 znaków.";
    case "EQUIPMENT_REJECTION_REASON_REQUIRED":
      return "Przy odrzuceniu podaj powód.";
    case "EQUIPMENT_SERIAL_TAKEN":
      return "Ten numer seryjny jest już na stanie firmy.";
    case "EQUIPMENT_STATUS_VIA_RPC":
    case "EQUIPMENT_PROTOCOL_VIA_RPC":
      return "Status sprzętu zmienia się tylko przez protokół przekazania.";
    default:
      return "Nie udało się zapisać protokołu sprzętu. Spróbuj ponownie.";
  }
}

export function parseEquipmentErrorCode(error: unknown): string {
  if (error instanceof EquipmentApiError) return error.code;

  const rec = error && typeof error === "object" ? (error as Record<string, unknown>) : null;
  const text = [rec?.message, rec?.details, rec?.hint]
    .filter((part) => typeof part === "string")
    .join(" ");

  const match = text.match(/EQUIPMENT_[A-Z0-9_]+/);
  if (match) return match[0];

  if (rec?.code === "23505") {
    if (text.includes("equipment_assets_org_serial")) return "EQUIPMENT_SERIAL_TAKEN";
    if (text.includes("one_pending")) return "EQUIPMENT_ALREADY_PENDING";
    return "EQUIPMENT_ALREADY_PENDING";
  }

  return "EQUIPMENT_RPC_FAILED";
}
